import React, { useEffect, useState } from 'react';
import { enqueueSnackbar } from 'notistack';
import Box from '@mui/material/Box';
import { useTheme } from '@mui/material/styles';
import Divider from '@mui/material/Divider';
import Paper from '@mui/material/Paper';
import Typography from '@mui/material/Typography';
import AddIcon from '@mui/icons-material/Add';
import NotificationsIcon from '@mui/icons-material/Notifications';
import Button from '@mui/material/Button';
import { AnalysisOptions } from '../Widgets/AlertConfig/AnalysisOptionsFormSection';
import {
  Alert,
  AlertParam,
  ChecklistType,
  makeDefaultAlertParam,
} from '../Widgets/AlertConfig/helpers';
import {
  addNewLocationAlert,
  addNewSensorAlert,
  deleteLocationAlert,
  deleteSensorAlert,
  getLocationAlertConfig,
  getSensorAlertConfig,
  updateLocationAlertStatus,
  updateSensorAlertStatus,
} from '../services/apiService';
import {
  Analysisoptions,
  Analysisoptions1,
  LocationAlertParamCreate,
  SensorAlertParamCreate,
} from '../services/api';
import { themeProps } from '../styles/theme';
import { useAppDispatch } from '../state/store';
import { setAlertsById } from '../state/actions';
import AlertParamAccordion from '../Widgets/AlertConfig/AlertParamAccordion';

export interface AlertConfigProps {
  checkListId: string;
  checklistType: ChecklistType;
  params?: AlertParam[];
  setParams?: (value: AlertParam[]) => void;
}

// Grouped by isLocation to avoid repeating the ternary everywhere
const getApi = (isLocation: boolean) =>
  isLocation
    ? { fetch: getLocationAlertConfig, add: addNewLocationAlert, remove: deleteLocationAlert }
    : { fetch: getSensorAlertConfig, add: addNewSensorAlert, remove: deleteSensorAlert };

function AlertConfig({ checkListId, checklistType, params, setParams }: AlertConfigProps) {
  const theme = useTheme();
  const dispatch = useAppDispatch();
  const isLocation = checklistType === ChecklistType.location;
  const api = getApi(isLocation);

  const [alertParams, setAlertParams] = useState<AlertParam[]>([]);

  useEffect(() => {
    if (params) setAlertParams(params);
  }, [params]);

  const handleError = (err: unknown) =>
    enqueueSnackbar((err as { cause: string }).cause, { variant: 'error', persist: true });

  const syncAlerts = (res: Alert) => {
    dispatch(setAlertsById([res]));
    setAlertParams(res.alerts);
    if (setParams) setParams(res.alerts);
  };

  useEffect(() => {
    if (!params) {
      api.fetch(checkListId).then((res) => {
        dispatch(setAlertsById([res]));
        setAlertParams(res.alerts.length > 0 ? res.alerts : []);
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [checkListId, isLocation]);

  const updateAlertParam = (index: number, patch: Partial<AlertParam>) =>
    setAlertParams((prev) =>
      prev.map((p, i) => (i === index ? ({ ...p, ...patch } as AlertParam) : p))
    );

  const updateAnalysisOptions = (index: number, patch: Partial<AnalysisOptions>) =>
    setAlertParams((prev) =>
      prev.map((p, i) =>
        i === index
          ? ({ ...p, analysisOptions: { ...p.analysisOptions, ...patch } } as AlertParam)
          : p
      )
    );

  const addAlertParam = () =>
    setAlertParams((prev) => [
      ...prev,
      { ...makeDefaultAlertParam(isLocation), alertId: prev.length },
    ]);

  type CreateParam = LocationAlertParamCreate & SensorAlertParamCreate;

  const buildCreateParam = (alertParam: AlertParam): CreateParam =>
    ({
      alertMethods: alertParam.alertMethods,
      analysisOptions: alertParam.analysisOptions as Analysisoptions & Analysisoptions1,
      uiOptions: alertParam.uiOptions ?? null,
    } as CreateParam);

  const handleSubmit = (alertParam: AlertParam) => {
    (api.add as (id: string, p: CreateParam) => Promise<Alert>)(
      checkListId,
      buildCreateParam(alertParam)
    )
      .then((res) => {
        syncAlerts(res);
        enqueueSnackbar('Alert successfully created', { variant: 'success' });
      })
      .catch(handleError);
  };

  const handleDelete = (alertId: number) => {
    (api.remove as (alertId: number, id: string) => Promise<Alert>)(alertId, checkListId)
      .then((res) => {
        syncAlerts(res);
        enqueueSnackbar('Alert successfully deleted', { variant: 'success' });
      })
      .catch(handleError);
  };

  const handleUpdate = (alertId: number, alertParam: AlertParam) => {
    (api.remove as (alertId: number, id: string) => Promise<Alert>)(alertId, checkListId)
      .then((deleteRes) => {
        syncAlerts(deleteRes);
        return (api.add as (id: string, p: CreateParam) => Promise<Alert>)(
          checkListId,
          buildCreateParam(alertParam)
        );
      })
      .then((createRes) => {
        syncAlerts(createRes);
        enqueueSnackbar('Alert successfully updated', { variant: 'success' });
      })
      .catch((err) => {
        enqueueSnackbar('Failed to update alert — it may have been removed without a replacement', {
          variant: 'error',
        });
        handleError(err);
      });
  };

  const changeStatus = (alertParam: AlertParam) => {
    const { alertStatus } = alertParam;
    const hasExistingAlert = !!alertStatus;
    const hasActiveAlert = hasExistingAlert && alertStatus.isActive === true;

    if (isLocation) {
      updateLocationAlertStatus(alertParam.alertId, !hasActiveAlert, checkListId)
        .then((res) => {
          setAlertParams(res.alerts);
          dispatch(setAlertsById([res]));
          enqueueSnackbar('Alert successfully updated', { variant: 'success' });
        })
        .catch((err) => enqueueSnackbar(err.cause, { variant: 'error', persist: true }));
    } else {
      updateSensorAlertStatus(alertParam.alertId, !hasActiveAlert, checkListId)
        .then((res) => {
          setAlertParams(res.alerts);
          dispatch(setAlertsById([res]));
          enqueueSnackbar('Alert successfully updated', { variant: 'success' });
        })
        .catch((err) => enqueueSnackbar(err.cause, { variant: 'error', persist: true }));
    }
  };

  return (
    <Paper
      elevation={0}
      sx={{
        p: 3,
        border: '1px solid',
        borderColor: 'divider',
      }}
    >
      <Box sx={{ display: 'flex' }}>
        <NotificationsIcon sx={{ fontSize: '50px' }} color="warning" />
        <Box sx={{ ml: '10px' }}>
          <Typography variant="h6" gutterBottom>
            Configure Alerts for ({checkListId})
          </Typography>
          <p style={{ padding: 0, margin: 0 }}>Create alert rule</p>
        </Box>
      </Box>

      <Divider sx={{ border: `solid 0.5px ${theme.palette.primary.contrastText}50` }} />

      {alertParams.map((alertParam, index) => (
        <AlertParamAccordion
          alertParam={alertParam}
          index={index}
          isLocation={isLocation}
          onSave={handleSubmit}
          onDelete={handleDelete}
          onUpdate={handleUpdate}
          onChangeParam={(patch) => updateAlertParam(index, patch)}
          onChangeAnalysis={(patch) => updateAnalysisOptions(index, patch)}
          changeStatus={changeStatus}
        />
      ))}

      <Button
        onClick={addAlertParam}
        variant={themeProps.btnVariant.default}
        color="success"
        startIcon={<AddIcon />}
        size="small"
        sx={{ textTransform: 'capitalize', mt: 2 }}
      >
        Add Alert Param
      </Button>
    </Paper>
  );
}

AlertConfig.defaultProps = {
  params: undefined,
  setParams: undefined,
};

export default AlertConfig;
